import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux/es/hooks/useSelector";
import classes from './ChatTab.module.css';
import { API_ENDPOINT } from "../../services/api/resources";
import axios from "axios";
import { shouldMakeAPICall, chatRepository, rfqRepository } from "../../offline";
import { Badge, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import DButton from "../../components/Dashboard/DashboardButtons/Dbutton";

// icon import
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ManageAccountsRoundedIcon from '@mui/icons-material/ManageAccountsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import { AttachEmail, ForumOutlined, SearchRounded } from "@mui/icons-material";
import AttachmentList from "../../components/common/AttachmentList/AttachmentList";
import {
    applyLocalReadStateToMessages,
    isChatMessageUnreadForUser,
    markChatMessagesReadLocally,
} from "../../utils/chatReadState";

const Chat_System = () => {
    const navigate = useNavigate();

    const loginData = useSelector((state) => state.loginData.value);

    const [buyer_token] = useState(loginData?.user?.token);
    const [auth_token] = useState(loginData?.auth_token);
    const [user_token] = useState(loginData?.user?.user_token);
    const [accessType] = useState(loginData?.access_type)

    const [getuserlist, setUserList] = useState([]);
    const [allmessages, setAllMessages] = useState([]);
    const [activeTab, setActiveTab] = useState("Users");
    const [selectedchat, setSelectedChat] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);
    const [conversationMessages, setConversationMessages] = useState([]);
    const [getuserinput, setUserInput] = useState({ message: "", file: null, fileName: "" });
    const [searchTerm, setSearchTerm] = useState("");


    const [openRFQs, setOpenRFQs] = useState([]);


    const getAllUserList = async () => {
        try {
            const response = await axios.post(
                `${API_ENDPOINT.all_user_list}`,
                { buyer_token },
                { headers: { Authorization: `Bearer ${auth_token}` } }
            );
            const filteredUsers = response.data.filter(items => items.token !== user_token)
            console.log(filteredUsers)
            setUserList(filteredUsers);
        } catch (error) {
            console.error("Error fetching user list:", error);
        }
    };

    const getAllMessages = async () => {
        const formData = new FormData();
        formData.append("sender_token", user_token);

        try {
            const response = await axios.post(
                `${API_ENDPOINT.get_chat_messages}`,
                formData,
                { headers: { Authorization: `Bearer ${auth_token}` } }
            );
            setAllMessages(applyLocalReadStateToMessages(response.data, user_token));
        } catch (error) {
            console.error("Error fetching messages:", error);
        }
    };

    /** Flatten buyer_users[].chat for TransformMessages / allmessages. Ensure "created" for UI. */
    const flattenBuyerUsersChat = (buyerUsers) => {
        if (!Array.isArray(buyerUsers)) return [];
        const out = [];
        for (const u of buyerUsers) {
            if (!Array.isArray(u.chat)) continue;
            for (const m of u.chat) {
                out.push({
                    ...m,
                    created: m.created || m.created_date || m.created_on,
                    _user_token: u.token,
                    _user_id: u.id,
                });
            }
        }
        return out;
    };

    /** Offline: user list from buyer_users (exclude current user). */
    const userListFromBuyerUsers = (buyerUsers) => {
        if (!Array.isArray(buyerUsers)) return [];
        return buyerUsers
            .filter((u) => u.token && u.token !== user_token)
            .map((u) => ({
                id: u.id,
                token: u.token,
                first_name: u.first_name || u.name?.split?.(' ')?.[0] || '—',
                last_name: u.last_name || '',
                designation: u.designation || '—',
                role: u.role || 'User',
                company_name: u.company_name,
                email: u.email,
                rfq_id: u.chat?.some?.((m) => m.rfq_id != null) ? (u.chat.find((m) => m.rfq_id != null)?.rfq_id) : null,
            }));
    };

    /** Offline: load from IndexedDB (buyer_users + vendor lists) and set state. */
    const loadChatFromCache = async () => {
        try {
            const buyerUsers = await chatRepository.getBuyerUsersFromCache(buyer_token);
            const list = userListFromBuyerUsers(buyerUsers);
            setUserList(list);
            const flat = applyLocalReadStateToMessages(flattenBuyerUsersChat(buyerUsers), user_token);
            setAllMessages(flat);
            const vendorData = await rfqRepository.getAllVendorListsWithChatFromCache(buyer_token);
            setVendorChannelsOffline(vendorData);
            console.log('📦 Chat offline: loaded', list.length, 'users,', flat.length, 'messages,', vendorData.length, 'RFQ vendor lists');
        } catch (e) {
            console.warn('Chat loadChatFromCache error:', e);
        }
    };

    const [vendorChannelsOffline, setVendorChannelsOffline] = useState([]);

    const normalizeSearchValue = (value) =>
        String(value ?? "")
            .toLowerCase()
            .trim();

    const messageTimestamp = (value) => {
        if (!value) return 0;
        const parsed = new Date(String(value).replace(" - ", " "));
        const timestamp = parsed.getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
    };

    const latestMessage = (messages = []) =>
        [...messages].sort((a, b) => messageTimestamp(a?.created) - messageTimestamp(b?.created)).at(-1) || null;

    const messagePreview = (message) => {
        if (!message) return "No messages yet";
        const text = String(message?.message ?? "").trim();
        if (text) return text;
        if (message?.fileName) return `Attachment: ${message.fileName}`;
        if (Array.isArray(message?.document) && message.document.length > 0) return "Attachment";
        return "Message";
    };

    const isSelectedConversation = (person, mode) => {
        if (mode === "rfq") {
            return String(selectedchat?.user || "") === String(person?.user || "");
        }
        return String(selectedchat?.id || selectedchat?.token || "") === String(person?.id || person?.token || "");
    };

    const formatRelativeTime = (value) => {
        const timestamp = messageTimestamp(value);
        if (!timestamp) return "";
        const diffMs = Date.now() - timestamp;
        const diffMinutes = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMinutes < 1) return "now";
        if (diffMinutes < 60) return `${diffMinutes}m`;
        if (diffHours < 24) return `${diffHours}h`;
        if (diffDays < 7) return `${diffDays}d`;
        return new Date(timestamp).toLocaleDateString("en-IN", { month: "short", day: "numeric" });
    };

    const markConversationReadLocally = (predicate) => {
        const readMessages = allmessages.filter((message) => predicate(message));
        setAllMessages((prev) =>
            prev.map((message) => {
                if (!predicate(message)) return message;
                return { ...message, status: true };
            }),
        );
        markChatMessagesReadLocally(readMessages, user_token);
    };

    useEffect(() => {
        const online = shouldMakeAPICall(auth_token, buyer_token);
        if (!online) {
            loadChatFromCache();
            return;
        }
        (async () => {
            await chatRepository.syncBuyerUsersFromAPI(auth_token, buyer_token, user_token, accessType);
        })();
        getAllUserList();
        getAllMessages();
    }, []);

    useEffect(() => {
        const online = shouldMakeAPICall(auth_token, buyer_token);
        if (!online) return undefined;

        const intervalId = window.setInterval(() => {
            getAllUserList();
            getAllMessages();
        }, 15000);

        return () => window.clearInterval(intervalId);
    }, [auth_token, buyer_token, user_token]);


    const filteredUsers = useMemo(() => {
        return getuserlist.filter((user) => {
            if (activeTab === "Users") return user.role === "User";
            if (activeTab === "Buyers") return user.role === "Buyer";
            if (activeTab === "RFQ") return user.rfq_id !== null;
            return false;
        });
    }, [getuserlist, activeTab]);

    const userConversationList = useMemo(() => {
        const term = normalizeSearchValue(searchTerm);

        return filteredUsers
            .map((user) => {
                const threadMessages = allmessages.filter((message) => {
                    const isDirectThread =
                        (!message.rfq_id || message.rfq_id === "" || message.rfq_id == null) &&
                        ((String(message.sender_token) === String(user.token) && String(message.receiver_token) === String(user_token)) ||
                            (String(message.sender_token) === String(user_token) && String(message.receiver_token) === String(user.token)));
                    return isDirectThread;
                });

                const lastMessage = latestMessage(threadMessages);
                const unreadCount = threadMessages.filter(
                    (message) =>
                        String(message.sender_token) === String(user.token) &&
                        String(message.receiver_token) === String(user_token) &&
                        isChatMessageUnreadForUser(message, user_token),
                ).length;

                return {
                    ...user,
                    lastMessage,
                    unreadCount,
                    preview: messagePreview(lastMessage),
                };
            })
            .filter((user) => {
                if (!term) return true;
                return [
                    user.first_name,
                    user.last_name,
                    user.designation,
                    user.company_name,
                    user.email,
                    user.preview,
                ]
                    .filter(Boolean)
                    .some((value) => normalizeSearchValue(value).includes(term));
            })
            .sort(
                (a, b) =>
                    messageTimestamp(b.lastMessage?.created || b.lastCreated) -
                    messageTimestamp(a.lastMessage?.created || a.lastCreated),
            );
    }, [allmessages, filteredUsers, searchTerm, user_token]);



    const handlerespectiveChat = async (person, sender_token, receiver_token, rfq_id = null) => {
        const isOffline = !shouldMakeAPICall(auth_token, buyer_token);

        if (rfq_id != null) {
            // RFQ-based chat (user or vendor)
            setSelectedChat({
                sender_token,
                receiver_token,
                rfq_id,
                first_name: getuserlist.find(i => i.token === receiver_token)?.first_name ?? person?.name ?? person?.first_name ?? "Unknown",
                user: person?.user,
                isVendor: person?.isVendor,
                vendor_token: person?.vendor_token,
                rfq_token: person?.rfq_token,
            });
            markConversationReadLocally((message) => {
                if (String(message?.rfq_id ?? "") !== String(rfq_id ?? "")) return false;
                const senderMatch = String(message?.sender_token ?? "") === String(sender_token ?? "");
                const receiverMatch = String(message?.receiver_token ?? "") === String(receiver_token ?? "");
                const reverseSenderMatch = String(message?.sender_token ?? "") === String(receiver_token ?? "");
                const reverseReceiverMatch = String(message?.receiver_token ?? "") === String(sender_token ?? "");
                return (senderMatch && receiverMatch) || (reverseSenderMatch && reverseReceiverMatch);
            });
        } else {
            // Direct user chat
            setSelectedChat({
                token: person.token,
                first_name: person.first_name,
            });
            markConversationReadLocally((message) => {
                const directThread =
                    (!message.rfq_id || message.rfq_id === "" || message.rfq_id == null) &&
                    ((String(message?.sender_token ?? "") === String(person?.token ?? "") &&
                        String(message?.receiver_token ?? "") === String(user_token ?? "")) ||
                        (String(message?.sender_token ?? "") === String(user_token ?? "") &&
                            String(message?.receiver_token ?? "") === String(person?.token ?? "")));
                return directThread;
            });
        }

        if (isOffline) {
            // Use cached buyer_users chat or vendor channels
            if (person?.isVendor && person?.vendor_token != null && (person?.rfq_id != null || person?.rfq_token != null)) {
                const v = vendorChannelsOffline.find((x) =>
                    (person.rfq_id != null && x.rfq_id != null && String(x.rfq_id) === String(person.rfq_id)) ||
                    (person.rfq_token && x.rfq_token === person.rfq_token)
                );
                const vendors = v?.vendors ?? [];
                const vendor = vendors.find((y) => y.vendor_token === person.vendor_token);
                const chat = vendor?.chat ?? [];
                const mapped = chat.map((m) => ({
                    message: m.message,
                    created: m.created_date || m.created,
                    document: m.document || [],
                    isSender: !!(m.sender_token === user_token || (m.sender_role && /buyer|teckost/i.test(String(m.sender_role)))),
                }));
                setConversationMessages(mapped);
                return;
            }
            const buyerUsers = await chatRepository.getBuyerUsersFromCache(buyer_token);
            const flat = flattenBuyerUsersChat(buyerUsers);
            let filtered = [];
            if (rfq_id != null && sender_token != null && receiver_token != null) {
                filtered = flat.filter((m) => String(m.rfq_id) === String(rfq_id) && (
                    (m.sender_token === sender_token && m.receiver_token === receiver_token) ||
                    (m.sender_token === receiver_token && m.receiver_token === sender_token)
                ));
            } else {
                const other = person?.token;
                if (other) {
                    filtered = flat.filter((m) => {
                        const a = m.sender_token === user_token && m.receiver_token === other;
                        const b = m.sender_token === other && m.receiver_token === user_token;
                        return (a || b) && (m.rfq_id == null || m.rfq_id === undefined || m.rfq_id === '');
                    });
                }
            }
            setConversationMessages(filtered);
            return;
        }

        const usertokens = rfq_id != null
            ? { sender_token, receiver_token, rfq_id }
            : { sender_token: user_token, receiver_token: person.token };

        try {
            const response = await axios.post(
                `${API_ENDPOINT.get_chat_messages}`,
                usertokens,
                { headers: { Authorization: `Bearer ${auth_token}` } }
            );
            const filteredMessages = (response.data || []).filter((msg) => msg.rfq_id == rfq_id);
            setConversationMessages(filteredMessages);
        } catch (error) {
            console.error("Error fetching conversation messages:", error);
            setConversationMessages([]);
        }
    };


    const handleUserMessageInput = (evt) => {
        const inputVal = evt.target.value;
        setUserInput({ ...getuserinput, message: inputVal });
    };

    const handleUserSendMessage = async () => {


        // const receiver_data = {
        //     sender_token: user_token,
        //     receiver_token: selectedchat.receiver_token ? selectedchat.receiver_token : selectedchat.token,
        //     sender_role: accessType,
        //     message: getuserinput.message,
        //     rfq_token: selectedchat?.rfq_id ? selectedchat.rfq_id : ''
        // }

        if (!getuserinput.message && !getuserinput.file) return; // Prevent sending empty messages

        const formData = new FormData();
        formData.append("sender_token", user_token);
        formData.append("receiver_token", selectedchat.receiver_token ? selectedchat.receiver_token : selectedchat.token);
        formData.append("sender_role", accessType);
        formData.append("message", getuserinput.message)
        formData.append("rfq_token", selectedchat?.rfq_id ? selectedchat.rfq_id : '')


        if (getuserinput.file) {
            formData.append("rfq_doc", getuserinput.file); // Correctly append the file
        } else {
            console.warn("No file selected");
        }


        const res = await axios.post(API_ENDPOINT.user_chat_message, formData, {
            headers: {
                Authorization: `Bearer ${auth_token}`,
                "Content-Type": "multipart/form-data",
            },
        });


        setConversationMessages(prev => [
            ...prev,
            {
                sender_token: user_token,
                receiver_token: selectedchat.token,
                attachment: getuserinput.file ? URL.createObjectURL(getuserinput.file) : null,
                fileName: getuserinput.file ? getuserinput.file.name : null,
                message: getuserinput.message,
                created: new Date().toLocaleString(),
                isSender: true,
            },
        ]);
        setUserInput({ message: "", file: null, fileName: "" });

    }


    const TransformMessages = (messages, user_token, getuserlist) => {
        return useMemo(() => {
            const result = {};

            for (const message of messages) {
                const { rfq_id, receiver_token, sender_token, name, designation } = message;


                let curr_sender_token, curr_receiver_token;

                if (receiver_token === user_token) {
                    curr_sender_token = user_token;
                    curr_receiver_token = sender_token;
                } else if (sender_token === user_token) {
                    curr_sender_token = sender_token;
                    curr_receiver_token = receiver_token;
                } else {
                    curr_sender_token = receiver_token;
                    curr_receiver_token = sender_token;
                }

                if (!rfq_id) continue;

                if (!result[rfq_id]) {
                    result[rfq_id] = [];
                }

                let userRef = result[rfq_id].find(entry =>
                    ((entry.sender_token === curr_sender_token) && (entry.receiver_token === curr_receiver_token)) ||
                    ((entry.sender_token === curr_receiver_token) && (entry.receiver_token === curr_sender_token))
                );

                if (!userRef) {
                    userRef = {
                        sender_token: curr_sender_token,
                        receiver_token: curr_receiver_token,
                        name: getuserlist.find(i => i.token === curr_receiver_token)?.first_name ?? "Unknown",
                        designation: getuserlist.find(i => i.token === curr_receiver_token)?.designation ?? "Unknown",
                        rfq_id: rfq_id,
                        messages: [],
                        user: `${curr_sender_token}_${curr_receiver_token}`,
                    };
                    result[rfq_id].push(userRef);
                }

                userRef.messages.push({
                    sender_token: sender_token,
                    sender: name,
                    receiver_token: receiver_token,
                    receiver: getuserlist.find(i => i.token === receiver_token)?.first_name ?? "Unknown",
                    message: message.message,
                    rfq_id: rfq_id,
                    status: message.status,
                    created: message.created
                });
            }

            return result;
        }, [messages, user_token, getuserlist]);
    };



    const final_data = TransformMessages(allmessages, user_token, getuserlist);

    /** Merge user threads (final_data) + vendor channels for RFQ tab. Keys: rfq_id (or rfq_token). */
    const rfqTabData = useMemo(() => {
        const out = { ...final_data };
        if (!Array.isArray(vendorChannelsOffline)) return out;
        for (const { rfq_id, rfq_token, vendors } of vendorChannelsOffline) {
            const k = rfq_id != null ? String(rfq_id) : (rfq_token || '');
            if (!k) continue;
            const existing = out[k] ?? [];
            const vendorThreads = (vendors || []).map((v) => {
                const chat = v.chat ?? [];
                const last = chat.length ? chat[chat.length - 1] : null;
                const name = v.vendor_name || v.vendor_email || 'Vendor';
                return {
                    isVendor: true,
                    vendor_token: v.vendor_token,
                    rfq_id,
                    rfq_token,
                    first_name: name,
                    name,
                    designation: 'Vendor',
                    user: `vendor_${v.vendor_token}`,
                    sender_token: null,
                    receiver_token: v.vendor_token,
                    lastCreated: last?.created_date || last?.created || '',
                };
            });
            out[k] = [...existing, ...vendorThreads];
        }
        return out;
    }, [final_data, vendorChannelsOffline]);

    const rfqConversationList = useMemo(() => {
        const term = normalizeSearchValue(searchTerm);

        return Object.entries(rfqTabData)
            .flatMap(([rfqKey, value]) =>
                (Array.isArray(value) ? value : []).map((person) => {
                    const threadMessages = Array.isArray(person.messages) ? person.messages : [];
                    const lastMessage = latestMessage(threadMessages);
                    const unreadCount = threadMessages.filter(
                        (message) =>
                            String(message.sender_token) !== String(user_token) &&
                            isChatMessageUnreadForUser(message, user_token),
                    ).length;

                    return {
                        ...person,
                        rfq_key: rfqKey,
                        lastMessage,
                        unreadCount,
                        preview: messagePreview(lastMessage),
                    };
                }),
            )
            .filter((person) => {
                if (!term) return true;
                return [
                    person.name,
                    person.first_name,
                    person.designation,
                    person.preview,
                    person.rfq_key,
                ]
                    .filter(Boolean)
                    .some((value) => normalizeSearchValue(value).includes(term));
            })
            .sort((a, b) => messageTimestamp(b.lastMessage?.created || b.lastCreated) - messageTimestamp(a.lastMessage?.created || a.lastCreated));
    }, [rfqTabData, searchTerm, user_token]);

    const toggleAccordion = (rfq_id) => {
        setOpenRFQs((prev) =>
            prev.includes(rfq_id)
                ? prev.filter((id) => id !== rfq_id)
                : [...prev, rfq_id]
        );
    };


    useEffect(() => {
        setSelectedChat(null)
        setConversationMessages([])
    }, [activeTab])






    return (

        <div className={ classes.chatwrapper }>
            <div className={ classes.chatTopBar }>
                <div className={ classes.chatTopBarLeft }>
                    <h2 className={ classes.chatPageTitle }>Chat</h2>
                    <p className={ classes.chatPageSubtitle }>Manage direct and RFQ conversations</p>
                </div>

                <DButton
                    label={ "Back" }
                    btnHeight="4rem"
                    btnWidth="12rem"
                    buyerBtn={ true }
                    onClick={ () => navigate(-1) }
                />
            </div>
            <div className={ classes.chatcontainer }>
                <div className={ classes.chatsidebar }>
                    <div className={ classes.optionbtncontainer }>
                        <button className={ `${activeTab === "Users" ? classes.activetabbtn : classes.optioncontainerbutton}` } onClick={ () => setActiveTab('Users') }>
                            <AccountCircleRoundedIcon sx={ { fontSize: 28 } } />

                            <span>Users</span>
                        </button>
                        <button className={ `${activeTab === 'Buyers' ? classes.activetabbtn : classes.optioncontainerbutton}` } onClick={ () => setActiveTab('Buyers') }>
                            <ManageAccountsRoundedIcon sx={ { fontSize: 28 } } />

                            <span>Buyers</span>
                        </button>
                        <button className={ `${activeTab === 'RFQ' ? classes.activetabbtn : classes.optioncontainerbutton}` } onClick={ () => setActiveTab('RFQ') }>
                            <PeopleAltRoundedIcon sx={ { fontSize: 28 } } />

                            <span>RFQ</span>
                        </button>
                    </div>
                    <div className={ classes.usercontainer }>
                        <div className={ classes.searchContainer }>
                            <div className={ classes.searchField }>
                                <SearchRounded className={ classes.searchIcon } />
                                <input
                                    type="text"
                                    value={ searchTerm }
                                    onChange={ (e) => setSearchTerm(e.target.value) }
                                    placeholder={ `Search ${activeTab === "RFQ" ? "RFQ threads" : activeTab.toLowerCase()}...` }
                                />
                                { searchTerm ? (
                                    <Tooltip title="Clear search">
                                        <IconButton size="small" className={ classes.searchClearBtn } onClick={ () => setSearchTerm("") }>
                                            <CloseRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ) : null }
                            </div>
                        </div>

                        <div className={ classes.user }>
                            <div className={ classes.listHeaderRow }>
                                <h3 className={ classes.sectionTitle }>{ activeTab }</h3>
                                <span className={ classes.listCount }>
                                    { activeTab === "RFQ" ? rfqConversationList.length : userConversationList.length } chats
                                </span>
                            </div>
                            {
                                activeTab === "RFQ"
                                    ? rfqConversationList.length > 0
                                        ? rfqConversationList.map((person) => {
                                            const displayName = String(person.name || person.first_name || "Unknown");
                                            const unread = Number(person.unreadCount || 0);
                                            return (
                                                <div
                                                    key={ `${person.rfq_key}_${person.user ?? person.vendor_token ?? person.receiver_token}` }
                                                    className={ `${classes.chatuser} ${isSelectedConversation(person, "rfq") ? classes.active : ""} ${unread > 0 ? classes.unread : ""}` }
                                                    onClick={ () => handlerespectiveChat(person, person.sender_token, person.receiver_token, person.rfq_id) }
                                                >
                                                    <Badge
                                                        overlap="circular"
                                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                                        variant={ unread > 0 ? "dot" : "standard" }
                                                        color="primary"
                                                        invisible={ unread === 0 }
                                                    >
                                                        <div className={ classes.avatarCircle }>
                                                            <h3>{ displayName.charAt(0).toUpperCase() }</h3>
                                                        </div>
                                                    </Badge>
                                                    <div className={ classes.chatuserinfo }>
                                                        <h3>{ displayName.toUpperCase() }</h3>
                                                        <p>{ person.designation }</p>
                                                        <p className={ classes.messagePreview }>{ person.preview }</p>
                                                    </div>
                                                    <div className={ classes.threadMeta }>
                                                        <span className={ classes.timeBadge }>{ formatRelativeTime(person.lastMessage?.created || person.lastCreated) }</span>
                                                        { unread > 0 ? <span className={ classes.unreadCount }>{ unread > 9 ? "9+" : unread }</span> : null }
                                                    </div>
                                                </div>
                                            );
                                        })
                                        : (
                                            <div className={ classes.noResults }>No RFQ chats found.</div>
                                        )
                                    : userConversationList.length > 0
                                        ? userConversationList.map((person) => {
                                            const displayName = String(person.first_name || "Unknown");
                                            const unread = Number(person.unreadCount || 0);
                                            return (
                                                <div
                                                    key={ person.id }
                                                    className={ `${classes.chatuser} ${isSelectedConversation(person, "direct") ? classes.active : ""} ${unread > 0 ? classes.unread : ""}` }
                                                    onClick={ () => handlerespectiveChat(person) }
                                                >
                                                    <Badge
                                                        overlap="circular"
                                                        anchorOrigin={{ vertical: "top", horizontal: "right" }}
                                                        variant={ unread > 0 ? "dot" : "standard" }
                                                        color="primary"
                                                        invisible={ unread === 0 }
                                                    >
                                                        <div className={ classes.avatarCircle }>
                                                            <h3>{ displayName.charAt(0).toUpperCase() }</h3>
                                                        </div>
                                                    </Badge>
                                                    <div className={ classes.chatuserinfo }>
                                                        <h3>{ displayName.toUpperCase() }</h3>
                                                        <p>{ person.designation }</p>
                                                        <p className={ classes.messagePreview }>{ person.preview }</p>
                                                    </div>
                                                    <div className={ classes.threadMeta }>
                                                        <span className={ classes.timeBadge }>{ formatRelativeTime(person.lastMessage?.created) }</span>
                                                        { unread > 0 ? <span className={ classes.unreadCount }>{ unread > 9 ? "9+" : unread }</span> : null }
                                                    </div>
                                                </div>
                                            );
                                        })
                                        : (
                                            <div className={ classes.noResults }>No users found.</div>
                                        )
                            }
                        </div>
                    </div>
                </div>

                <div className={ classes.chatwindow }>
                    { selectedchat ? <div className={ classes.messageconatiner }>

                        <div className={ classes.chatheaderwrapper }>
                            <div className={ classes.userheader }>

                                <div className={ classes.avatarCircle }>
                                    <p>{
                                        selectedchat?.first_name?.charAt(0)?.toUpperCase() || "?"
                                    }</p>
                                </div>
                                <h3>{ String(selectedchat?.first_name || "Unknown").toUpperCase() }</h3>
                            </div>

                            {/* <div>
                                <button
                                    // onClick={handleDownloadChat}
                                    className={`${classes.downloadButton} ${isDownloading ? classes.downloading : ""}`}
                                    disabled={isDownloading}
                                >
                                    {isDownloading ? "Downloading..." : "Download Chat"}
                                </button>
                            </div> */}
                        </div>

                        <div className={ classes.chatmessages }>
                            { conversationMessages.length > 0 ? (
                                conversationMessages.map((msg, index) => {
                                    return (
                                        <div
                                            key={ index }
                                            className={ `${classes.messagewrapper} ${msg.isSender ? classes.sentwrapper : classes.receivedwrapper}` }
                                        >
                                            { !msg.isSender && (
                                                <div className={ classes.avatarCircle }>
                                                    <h3>
                                                        { selectedchat?.first_name
                                                            ? selectedchat.first_name.charAt(0).toUpperCase()
                                                            : "?" }
                                                    </h3>
                                                </div>
                                            ) }

                                            <div className={ `${classes.message} ${msg.isSender ? classes.sent : classes.received}` }>
                                                <p className={ classes.messageText }>{ msg.message }</p>
                                                <span>

                                                    { msg.document && msg.document.length > 0 ? <AttachmentList item={ msg } /> : '' }

                                                    { msg.attachment && (

                                                        <a href={ msg.attachment } target="_blank" rel="noopener noreferrer">
                                                            📎 { msg.fileName || "View Attachment" }
                                                        </a>

                                                    ) }
                                                </span>
                                                <p className={ classes.messageTime }>{ msg.created }</p>
                                            </div>

                                            { msg.isSender && (
                                                <div className={ classes.avatarCircle }>
                                                    <h3>{ loginData?.user?.first_name?.charAt(0)?.toUpperCase() || "?" }</h3>
                                                </div>
                                            ) }
                                        </div>
                                    )
                                })
                            ) : (
                                <div className={ classes.nomessages }>

                                </div>
                            ) }
                        </div>

                        <div className={ classes.chatinput }>
                            { getuserinput.fileName && (
                                <div className={ classes.filePreview }>
                                    <p>Attachment: { getuserinput.fileName }</p>
                                </div>
                            ) }

                            {/* Message Input Field */ }
                            <div className={ classes.inputwrapper }>
                                <input
                                    type="text"
                                    placeholder="Type a message..."
                                    value={ getuserinput.message }
                                    onChange={ handleUserMessageInput }
                                    onKeyDown={ (e) => e.key === "Enter" && handleUserSendMessage() }
                                />
                                <div className={ classes.composerActions }>
                                    <IconButton component="label" className={ classes.actionButton } aria-label="Attach file">
                                        <AttachEmail sx={ { fontSize: 22 } } color="primary" />
                                        <input
                                            id="fileUpload"
                                            type="file"
                                            hidden
                                            onChange={ (e) => {
                                                const file = e.target.files[0];
                                                setUserInput({
                                                    ...getuserinput,
                                                    file: file,
                                                    fileName: file ? file.name : "",
                                                });
                                            } }
                                        />
                                    </IconButton>
                                    <IconButton
                                        className={ classes.sendButton }
                                        onClick={ handleUserSendMessage }
                                        disabled={ !getuserinput.message && !getuserinput.file }
                                        size="large"
                                        aria-label="Send message"
                                    >
                                        <SendRoundedIcon sx={ { fontSize: 20 } } />
                                    </IconButton>
                                </div>
                            </div>
                        </div>


                    </div> :
                        <div className={ classes.emptychatmessage }>
                            <div className={ classes.emptyState }>
                                <ForumOutlined className={ classes.emptyIcon } />
                                <h3>Select a chat to start messaging</h3>
                                <p>
                                    Choose from your existing conversations or start a new one
                                </p>
                            </div>
                        </div>
                    }
                </div>
            </div>
        </div>

    )



}

export default Chat_System;
